import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  BookOpen, 
  Users, 
  Settings, 
  PlusCircle,
  GraduationCap,
  User,
  LogOut
} from 'lucide-react';
import { authContext } from '../../context/index.js';

const Sidebar = ({ isOpen, onClose }) => {
  const { userData, logout, isTeacher } = useContext(authContext);
  const location = useLocation();

  const teacherNavItems = [
    { name: 'Dashboard', href: '/teacher/dashboard', icon: Home },
    { name: 'My Courses', href: '/teacher/courses', icon: BookOpen },
    { name: 'Create Course', href: '/teacher/courses/create', icon: PlusCircle },
    { name: 'Students', href: '/teacher/students', icon: Users },
    { name: 'Settings', href: '/teacher/settings', icon: Settings },
  ];

  const studentNavItems = [
    { name: 'Dashboard', href: '/student/dashboard', icon: Home },
    { name: 'Browse Courses', href: '/student/courses', icon: BookOpen },
    { name: 'My Enrollments', href: '/student/enrollments', icon: GraduationCap },
    { name: 'Profile', href: '/student/profile', icon: User },
    { name: 'Settings', href: '/student/settings', icon: Settings },
  ];

  const navItems = isTeacher() ? teacherNavItems : studentNavItems;

  const isActive = (href) => location.pathname === href;

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-64 bg-white dark:bg-gray-800 shadow-lg z-50 transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:fixed lg:shadow-none
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-secondary-200 dark:border-gray-700">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-accent-600 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-secondary-900 dark:text-white">SkillForge</span>
          </Link>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-secondary-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-secondary-900 dark:text-white">{userData?.name}</p>
              <p className="text-xs text-secondary-500 dark:text-gray-400 capitalize">{userData?.role}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`
                      flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                      ${isActive(item.href) 
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border-r-2 border-primary-600' 
                        : 'text-secondary-600 dark:text-gray-300 hover:bg-secondary-50 dark:hover:bg-gray-700 hover:text-secondary-900 dark:hover:text-white'
                      }
                    `}
                    onClick={onClose}
                  >
                    <Icon size={20} />
                    <span>{item.name}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-secondary-200 dark:border-gray-700">
          <button
            onClick={logout}
            className="flex items-center space-x-3 px-3 py-2 w-full text-left text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
